import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import {
  createTechnician, deleteTechnician,
  fetchSubtaskTechniciansBySubtask, deleteSubtaskTechniciansBySubtask, insertSubtaskTechniciansBatch,
  updateSubtask, createSubtask, updateProject,
} from '../lib/api'
import { logChange } from '../lib/auditLog'
import { addBusinessDays } from '../lib/businessDays'
import { DEPT_COLORS } from '../lib/deptColors'
import ProjectHistoryDrawer from './ProjectHistoryDrawer'

const STATUSES = ['Scheduled', 'In Work', 'Complete']

const DEPT_LABELS = {
  MX: 'Maintenance', AVI: 'Avionics', INT: 'Interior',
  PA: 'Paint', QA: 'Quality Assurance', EXT: 'External',
}

export default function SubtaskModal({
  projectId, subtask, userProfile, onClose, onSaved,
  defaultStartDate, defaultEndDate,
  technicians: techniciansProp = [],
}) {
  const { isTechnician, profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments
    ?? Object.keys(DEPT_COLORS).map(k => ({ key: k, label: DEPT_LABELS[k] || k, hex: DEPT_COLORS[k].hex }))
  const deptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))
  const [form, setForm] = useState({
    task_name: '',
    departments: [orgDepts[0]?.key ?? 'MX'],
    start_date: '',
    end_date: '',
    status: 'Scheduled',
  })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [pushDays, setPushDays] = useState('')
  const [showHistory, setShowHistory] = useState(false)

  // ── Technician state ──────────────────────────────────────────────────────
  const [localTechnicians, setLocalTechnicians] = useState(techniciansProp)
  const [selectedTechIds, setSelectedTechIds] = useState(new Set())
  const [expandedDepts, setExpandedDepts] = useState(new Set())
  const [addingTech, setAddingTech] = useState(null) // { dept, first_name, last_name }
  const [savingTech, setSavingTech] = useState(false)
  const [confirmDeleteTechId, setConfirmDeleteTechId] = useState(null)
  const [deletingTech, setDeletingTech] = useState(false)

  // ── Notes state (edit mode only) ─────────────────────────────────────────
  const [subtaskNotes, setSubtaskNotes]     = useState([])
  const [newSubtaskNote, setNewSubtaskNote] = useState('')
  const [savingNote, setSavingNote]         = useState(false)

  useEffect(() => {
    if (!subtask?.id) return
    supabase
      .from('project_notes')
      .select('*')
      .eq('subtask_id', subtask.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setSubtaskNotes(data ?? []))
  }, [subtask?.id])

  async function submitSubtaskNote() {
    if (!newSubtaskNote.trim() || savingNote) return
    setSavingNote(true)
    const name = userProfile?.display_name || userProfile?.email || null
    const { data, error } = await supabase
      .from('project_notes')
      .insert({
        project_id:    projectId,
        subtask_id:    subtask.id,
        subtask_name:  form.task_name || subtask.task_name,
        body:          newSubtaskNote.trim(),
        created_by:    userProfile?.id ?? null,
        created_by_name: name,
      })
      .select()
      .single()
    if (!error && data) {
      setSubtaskNotes(prev => [data, ...prev])
      setNewSubtaskNote('')
    }
    setSavingNote(false)
  }

  // Sync technicians from prop (in case prop updates while modal is open)
  useEffect(() => {
    setLocalTechnicians(techniciansProp)
  }, [techniciansProp])

  // Pre-populate form when editing
  useEffect(() => {
    if (subtask) {
      setForm({
        ...subtask,
        departments: subtask.departments?.length ? subtask.departments : [subtask.department || 'MX'],
      })
    } else if (defaultStartDate) {
      setForm(f => ({
        ...f,
        start_date: defaultStartDate,
        end_date: defaultEndDate || defaultStartDate,
      }))
    }
  }, [subtask, defaultStartDate, defaultEndDate])

  // Load existing technician assignments when editing
  useEffect(() => {
    if (subtask?.id) {
      fetchSubtaskTechniciansBySubtask(subtask.id)
        .then(({ data }) => {
          setSelectedTechIds(new Set((data || []).map(st => st.technician_id)))
        })
    }
  }, [subtask?.id])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const applyPush = () => {
    const n = parseInt(pushDays, 10)
    if (isNaN(n) || n === 0 || !form.start_date) return
    setForm(f => ({
      ...f,
      start_date: addBusinessDays(f.start_date, n),
      end_date:   f.end_date ? addBusinessDays(f.end_date, n) : f.end_date,
    }))
    setPushDays('')
  }

  // ── Technician helpers ────────────────────────────────────────────────────

  const toggleTech = (techId) => {
    setSelectedTechIds(prev => {
      const next = new Set(prev)
      next.has(techId) ? next.delete(techId) : next.add(techId)
      return next
    })
  }

  const toggleDept = (dept) => {
    setExpandedDepts(prev => {
      const next = new Set(prev)
      next.has(dept) ? next.delete(dept) : next.add(dept)
      return next
    })
  }

  const startAddTech = (e, dept) => {
    e.stopPropagation()
    setAddingTech({ dept, first_name: '', last_name: '' })
    // Auto-expand the dept when adding
    setExpandedDepts(prev => new Set([...prev, dept]))
  }

  const saveNewTech = async () => {
    if (!addingTech?.first_name?.trim() || !addingTech?.last_name?.trim()) return
    setSavingTech(true)
    try {
      const { data: newTech, error } = await createTechnician({
        first_name: addingTech.first_name.trim(),
        last_name: addingTech.last_name.trim(),
        department: addingTech.dept,
      })
      if (error) {
        console.error('Error creating technician:', error)
        setSaveError(`Failed to add technician: ${error.message}`)
        return
      }
      if (newTech) {
        setLocalTechnicians(prev => [...prev, newTech].sort((a, b) => a.last_name.localeCompare(b.last_name)))
        setSelectedTechIds(prev => new Set([...prev, newTech.id]))
        setAddingTech(null)
      }
    } catch (err) {
      console.error('Error saving technician:', err)
      setSaveError('Failed to add technician — please try again.')
    } finally {
      setSavingTech(false)
    }
  }

  const deleteTech = async (techId) => {
    setDeletingTech(true)
    try {
      const { error } = await deleteTechnician(techId)
      if (error) {
        setSaveError(`Failed to delete technician: ${error.message}`)
        return
      }
      setLocalTechnicians(prev => prev.filter(t => t.id !== techId))
      setSelectedTechIds(prev => {
        const next = new Set(prev)
        next.delete(techId)
        return next
      })
      setConfirmDeleteTechId(null)
    } catch (err) {
      console.error('Error deleting technician:', err)
      setSaveError('Failed to delete technician — please try again.')
    } finally {
      setDeletingTech(false)
    }
  }

  const syncTechnicians = async (subtaskId) => {
    // Delete all existing assignments for this subtask, then re-insert
    const { error: delErr } = await deleteSubtaskTechniciansBySubtask(subtaskId)
    if (delErr) throw new Error(`Failed to update technician assignments: ${delErr.message}`)

    if (selectedTechIds.size > 0) {
      const { error: insErr } = await insertSubtaskTechniciansBatch(
        [...selectedTechIds].map(techId => ({
          subtask_id: subtaskId,
          technician_id: techId,
        }))
      )
      if (insErr) throw new Error(`Failed to assign technicians: ${insErr.message}`)
    }
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  const save = async () => {
    if (!form.task_name || !form.start_date || !form.end_date) return
    setSaving(true)
    setSaveError(null)

    // Strip computed/view-only properties — keep only DB columns
    const { id, project_id, created_at, _registration, _label, _isSubtask, _color, _isPin, _project, department: _singleDept, ...subtaskFields } = form
    const depts = form.departments?.length ? form.departments : ['MX']
    const savePayload = { ...subtaskFields, department: depts[0], departments: depts }

    try {
      if (subtask) {
        const { error: upErr } = await updateSubtask(subtask.id, savePayload)
        if (upErr) throw new Error(`Failed to save subtask: ${upErr.message}`)
        await syncTechnicians(subtask.id)
        await logChange({
          projectId, entityType: 'subtask', entityId: subtask.id,
          entityLabel: form.task_name, action: 'updated',
          oldData: subtask, newData: savePayload, userProfile,
        })
      } else {
        const { data: created, error: insErr } = await createSubtask({ ...savePayload, project_id: projectId })
        if (insErr) throw new Error(`Failed to create subtask: ${insErr.message}`)
        if (created) {
          await syncTechnicians(created.id)
          await logChange({
            projectId, entityType: 'subtask', entityId: created.id,
            entityLabel: form.task_name, action: 'created', userProfile,
          })
        }
      }
      await updateProject(projectId, { workflow_as_of: new Date().toISOString() })
      onSaved()
      onClose()
    } catch (err) {
      console.error('Save error:', err)
      setSaveError(err.message || 'An error occurred — please try again.')
    } finally {
      setSaving(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{subtask ? 'Edit Subtask' : 'Add Subtask'}</h2>
          <div className="flex items-center gap-2">
            {subtask && !isTechnician && (
              <button onClick={() => setShowHistory(true)} title="View history" className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Task Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Task Name *</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              placeholder="e.g. Avionics inspection"
              value={form.task_name}
              onChange={e => set('task_name', e.target.value)}
            />
          </div>

          {/* Departments */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Departments</label>
            <div className="flex flex-wrap gap-1.5">
              {orgDepts.map(({ key: d, hex: dHex }) => {
                const selected = (form.departments || []).includes(d)
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      const cur = form.departments || ['MX']
                      if (selected) {
                        if (cur.length > 1) set('departments', cur.filter(x => x !== d))
                      } else {
                        set('departments', [...cur, d])
                      }
                    }}
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                      selected
                        ? 'border-transparent'
                        : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                    }`}
                    style={selected ? { backgroundColor: dHex + '20', color: dHex } : {}}
                  >
                    {selected && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dHex }} />}
                    {d}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date *</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                value={form.start_date}
                onChange={e => set('start_date', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date *</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                value={form.end_date}
                onChange={e => set('end_date', e.target.value)}
              />
            </div>
          </div>

          {/* Push task by N business days — edit mode only */}
          {subtask && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Push task by business days</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  placeholder="e.g. 3"
                  value={pushDays}
                  onChange={e => setPushDays(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyPush() } }}
                />
                <button
                  type="button"
                  onClick={applyPush}
                  disabled={!pushDays || isNaN(parseInt(pushDays, 10)) || parseInt(pushDays, 10) === 0 || !form.start_date}
                  className="text-sm font-medium text-brand-red border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50 disabled:opacity-40 transition-colors"
                >
                  Apply
                </button>
                {pushDays && !isNaN(parseInt(pushDays, 10)) && parseInt(pushDays, 10) !== 0 && (
                  <span className="text-xs text-gray-400">
                    {parseInt(pushDays, 10) > 0 ? `+${parseInt(pushDays, 10)}` : parseInt(pushDays, 10)} business day{Math.abs(parseInt(pushDays, 10)) !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              value={form.status}
              onChange={e => set('status', e.target.value)}
            >
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          {/* ── Technicians ─────────────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600">Assigned Technicians</label>
              {selectedTechIds.size > 0 && (
                <span className="text-xs bg-brand-red/10 text-brand-red px-2 py-0.5 rounded-full font-medium">
                  {selectedTechIds.size} selected
                </span>
              )}
            </div>

            <div className="space-y-1">
              {orgDepts.map(({ key: dept, hex: deptHex, label: deptLabel }) => {
                const deptTechs = localTechnicians.filter(t => t.department === dept)
                const selectedInDept = deptTechs.filter(t => selectedTechIds.has(t.id)).length
                const isExpanded = expandedDepts.has(dept)
                const isAddingHere = addingTech?.dept === dept

                return (
                  <div key={dept} className="rounded-lg border border-gray-100 overflow-hidden">
                    {/* Dept header */}
                    <button
                      type="button"
                      onClick={() => toggleDept(dept)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: deptHex }}
                        />
                        <span className="text-xs font-medium text-gray-700">{deptLabel ?? dept}</span>
                        {selectedInDept > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full leading-none font-medium"
                            style={{ backgroundColor: deptHex + '25', color: deptHex }}>
                            {selectedInDept}
                          </span>
                        )}
                        {deptTechs.length === 0 && !isAddingHere && (
                          <span className="text-xs text-gray-300 italic">No technicians</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => startAddTech(e, dept)}
                        className="text-xs text-brand-red hover:text-brand-red-dark font-medium px-2 py-0.5 rounded hover:bg-red-50 transition-colors"
                      >
                        + Add
                      </button>
                    </button>

                    {/* Expanded content */}
                    {(isExpanded || isAddingHere) && (
                      <div className="divide-y divide-gray-50">
                        {/* Existing technicians */}
                        {deptTechs.map(tech => (
                          confirmDeleteTechId === tech.id ? (
                            /* ── Inline delete confirmation ── */
                            <div key={tech.id} className="flex items-center justify-between px-3 py-2 bg-red-50 border-t border-red-100">
                              <span className="text-xs text-red-700 font-medium truncate">
                                Delete {tech.first_name} {tech.last_name}?
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteTechId(null)}
                                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-red-100 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteTech(tech.id)}
                                  disabled={deletingTech}
                                  className="text-xs font-semibold text-white bg-red-500 hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed px-2 py-1 rounded transition-colors"
                                >
                                  {deletingTech ? 'Deleting…' : 'Delete'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* ── Normal technician row ── */
                            <div
                              key={tech.id}
                              className="group flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors"
                            >
                              <label className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedTechIds.has(tech.id)}
                                  onChange={() => toggleTech(tech.id)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700 select-none truncate">
                                  {tech.first_name} {tech.last_name}
                                </span>
                              </label>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteTechId(tech.id)}
                                className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all p-0.5 rounded"
                                title="Delete technician"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )
                        ))}

                        {/* Add new technician inline form */}
                        {isAddingHere && (
                          <div className="px-3 py-3 bg-red-50 border-t border-red-100 space-y-2">
                            <div className="text-xs font-medium text-brand-red">New Technician</div>
                            {/* Name inputs — full width stacked row */}
                            <div className="flex gap-2">
                              <input
                                autoFocus
                                placeholder="First name"
                                value={addingTech.first_name}
                                onChange={e => setAddingTech(a => ({ ...a, first_name: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveNewTech(); if (e.key === 'Escape') setAddingTech(null) }}
                                className="flex-1 min-w-0 text-xs border border-red-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20 bg-white"
                              />
                              <input
                                placeholder="Last name"
                                value={addingTech.last_name}
                                onChange={e => setAddingTech(a => ({ ...a, last_name: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveNewTech(); if (e.key === 'Escape') setAddingTech(null) }}
                                className="flex-1 min-w-0 text-xs border border-red-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20 bg-white"
                              />
                            </div>
                            {/* Action buttons on their own row */}
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setAddingTech(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={saveNewTech}
                                disabled={savingTech || !addingTech.first_name.trim() || !addingTech.last_name.trim()}
                                className="text-xs font-semibold text-white bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
                              >
                                {savingTech ? 'Saving…' : 'Save Technician'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
          {/* ── End Technicians ──────────────────────────────────────────── */}

          {/* ── Notes (edit mode only) ──────────────────────────────────── */}
          {subtask?.id && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Task Notes</label>
              <div className="space-y-2">
                {subtaskNotes.map(note => (
                  <div key={note.id} className="bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 mb-0.5">
                      {note.created_by_name && (
                        <span className="text-xs font-semibold text-gray-700">{note.created_by_name}</span>
                      )}
                      <span className="text-xs text-gray-400">
                        {new Date(note.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap leading-snug">{note.body}</p>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSubtaskNote}
                    onChange={e => setNewSubtaskNote(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitSubtaskNote() } }}
                    placeholder="Add a note…"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  />
                  <button
                    type="button"
                    onClick={submitSubtaskNote}
                    disabled={savingNote || !newSubtaskNote.trim()}
                    className="text-xs font-medium bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ── End Notes ────────────────────────────────────────────────── */}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          {saveError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || savingTech || deletingTech || !!addingTech || !form.task_name || !form.start_date || !form.end_date}
              className="text-sm font-medium bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
              title={addingTech ? 'Finish adding the new technician first' : savingTech ? 'Saving technician...' : deletingTech ? 'Deleting technician...' : ''}
            >
              {saving ? 'Saving...' : subtask ? 'Save Changes' : 'Add Subtask'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {showHistory && subtask && (
      <ProjectHistoryDrawer
        projectId={projectId}
        projectLabel={form.task_name || subtask.task_name}
        subtaskId={subtask.id}
        subtaskLabel={form.task_name || subtask.task_name}
        subtaskStartDate={form.start_date}
        subtaskEndDate={form.end_date}
        onClose={() => setShowHistory(false)}
      />
    )}
    </>
  )
}
